import { useMemo, useState } from "react";
import {
  areBrandDisclaimerDefaultsDisabled,
  type BrandDisclaimerFont,
  resolveDisclaimerSpecFromDefaults,
} from "@hightouch/lib/ad-studio/disclaimer/brand-defaults";
import {
  DISABLED_DISCLAIMER_SPEC,
  type DisclaimerSpec,
} from "@hightouch/lib/ad-studio/disclaimer/types";
import {
  DEFAULT_PDF_IMAGE_FILE_TYPE,
  type DownloadOption,
  type ImageFileType,
  PNG_IMAGE_FILE_TYPE,
} from "src/pages/ad-studio/briefs/components/download-options";
import { canBuildLayeredExport } from "src/pages/ad-studio/layered-export/prepare-layered-export";
import { type DisclaimerOverride, applyOverride } from "./disclaimer-override";
import { adHasAiGeneratedMedia } from "./has-ai-generated-media";
import { useBrandDisclaimerDefaultsResolver } from "./use-brand-disclaimer-defaults";

// Stable fallbacks so the memos below don't re-run when a batch is omitted.
const EMPTY_PREVIEW_ITEMS: DisclaimerPreviewItem[] = [];
const EMPTY_FORMAT_OPTIONS: DownloadOption[] = [];

export interface DisclaimerPreviewItem {
  id: string;
  assetId?: string;
  label?: string;
  /** Ad markup (HTML or SVG) to render the preview from. */
  content: string;
  componentAssets: Record<string, string> | null;
  /** The ad's project brand_profile_id (null → workspace default). */
  brandProfileId: string | null;
}

/** The effective per-ad disclosure to apply on download. */
export interface DisclaimerDownloadResult {
  id: string;
  spec: DisclaimerSpec;
  font: BrandDisclaimerFont;
}

/** The static half of a selection: ads with markup to rasterize. */
export interface StaticExportSettings {
  /** Format choices to offer; empty hides the section (the caller's format is fixed). */
  formatOptions: DownloadOption[];
  /** Info banner below the settings, e.g. what the export leaves out. */
  notice?: { title: string; message?: string };
  /** False when every brand opted out of disclosures — hides the controls and
   * preview; the confirmed result carries the disabled spec for every ad. */
  showDisclosures: boolean;
  /**
   * Ads to preview with the disclosures applied. Pass a stable (memoized) array.
   * 0 items → no preview; 1 → single preview; >1 → a carousel across the ads.
   */
  previewItems: DisclaimerPreviewItem[];
  /**
   * Builds self-contained base64 `@font-face` rules for the fonts a given
   * markup string references. The dialog inlines only the fonts used by the
   * preview it's currently rendering
   */
  getFontRulesForMarkup: (markup: string) => Promise<string[]>;
  apiBase: string;
}

/**
 * The video half of a selection: composition ads rendered server-side. The
 * export API takes one disclosure for the whole batch, so there is no per-ad
 * override and no preview.
 */
export interface VideoExportSettings {
  /** Composition ads selected; shown in the section note. */
  count: number;
  /** Seeds the shared spec from the first brand that still wants a disclosure. */
  brandProfileIds: Array<string | null>;
}

/** The user's choices. A key is present only when its batch was passed in. */
export interface ExportSettingsResult {
  staticAssets?: {
    /** Selected output formats; empty only when the dialog offered none. */
    options: DownloadOption[];
    /** Encoding for the "images" option. */
    imageFileType: ImageFileType;
    /** Encoding for the "pdf" option's pages. */
    pdfImageFileType: ImageFileType;
    /** Each ad's effective disclosure spec + brand font (its own default, with any user overrides applied). */
    disclosures: DisclaimerDownloadResult[];
  };
  videoAssets?: { spec: DisclaimerSpec };
}

export interface ExportSettingsArgs {
  /** Ads with markup. Omit when the selection has none. */
  staticAssets?: StaticExportSettings;
  /** Composition ads whose brands still want a disclosure. Omit when there are none. */
  videoAssets?: VideoExportSettings;
}

export type ResolvedPreviewItem = {
  item: DisclaimerPreviewItem;
  spec: DisclaimerSpec;
  font: BrandDisclaimerFont;
};

/**
 * State for the export-settings step. The dialog frame owns it so the footer
 * can enable Download and build the result, while the body renders controls.
 *
 * Nothing resets on reopen: callers must unmount the dialog on close, or a
 * second export starts from the first one's choices.
 */
export function useExportSettingsState({
  staticAssets,
  videoAssets,
}: ExportSettingsArgs) {
  const resolveDefaults = useBrandDisclaimerDefaultsResolver();
  const previewItems = staticAssets?.previewItems ?? EMPTY_PREVIEW_ITEMS;
  const formatOptions = staticAssets?.formatOptions ?? EMPTY_FORMAT_OPTIONS;
  const apiBase = staticAssets?.apiBase ?? "";

  // Each ad's own resolved disclosure (brand defaults + AI-media detection) and
  // brand overlay font. The user's edits live in a separate override layer so an
  // untouched control leaves every ad following its own default.
  const resolvedItems = useMemo<ResolvedPreviewItem[]>(
    () =>
      previewItems.map((item) => {
        const defaults = resolveDefaults(item.brandProfileId);
        const hasAiGeneratedMedia = adHasAiGeneratedMedia({
          content: item.content,
          componentAssets: item.componentAssets,
          apiBase,
        });
        return {
          item,
          spec: resolveDisclaimerSpecFromDefaults(defaults, {
            hasAiGeneratedMedia,
          }),
          font: defaults.font,
        };
      }),
    [previewItems, resolveDefaults, apiBase],
  );

  // Layered export needs layerable HTML, so it's only offered when at least one selected
  // ad can produce one.
  const offeredFormats = useMemo(
    () =>
      previewItems.some((item) => canBuildLayeredExport(item.content))
        ? formatOptions
        : formatOptions.filter(
            (option) => option !== "psd" && option !== "idml",
          ),
    [formatOptions, previewItems],
  );

  const [override, setOverride] = useState<DisclaimerOverride>({});
  // A lone offered format has no checkbox, so it starts checked.
  const [selectedOptions, setSelectedOptions] = useState<Set<DownloadOption>>(
    () => new Set(offeredFormats.length === 1 ? offeredFormats : ["images"]),
  );
  const [imageFileType, setImageFileType] =
    useState<ImageFileType>(PNG_IMAGE_FILE_TYPE);
  const [pdfImageFileType, setPdfImageFileType] = useState<ImageFileType>(
    DEFAULT_PDF_IMAGE_FILE_TYPE,
  );
  // The default derives from the current args, so a caller can mount this before its ads load.
  const [chosenVideoSpec, setVideoSpec] = useState<DisclaimerSpec>();
  // Motion ads are Ad Studio output, so they always count as AI-generated
  // media — same assumption the single-video export makes.
  const videoSpec =
    chosenVideoSpec ??
    resolveDisclaimerSpecFromDefaults(
      (videoAssets?.brandProfileIds ?? [])
        .map(resolveDefaults)
        .find((defaults) => !areBrandDisclaimerDefaultsDisabled(defaults)) ??
        resolveDefaults(null),
      { hasAiGeneratedMedia: true },
    );

  const toggleOption = (option: DownloadOption) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
      return next;
    });
  };

  const showFormats = offeredFormats.length > 0;
  const showStaticDisclosures =
    staticAssets !== undefined &&
    staticAssets.showDisclosures &&
    resolvedItems.length > 0;
  const selectedCount = offeredFormats.filter((o) =>
    selectedOptions.has(o),
  ).length;

  const buildResult = (): ExportSettingsResult => ({
    ...(staticAssets
      ? {
          staticAssets: {
            options: offeredFormats.filter((o) => selectedOptions.has(o)),
            imageFileType,
            pdfImageFileType,
            disclosures: resolvedItems.map((r) => ({
              id: r.item.id,
              spec: staticAssets.showDisclosures
                ? applyOverride(r.spec, override)
                : DISABLED_DISCLAIMER_SPEC,
              font: r.font,
            })),
          },
        }
      : {}),
    ...(videoAssets ? { videoAssets: { spec: videoSpec } } : {}),
  });

  return {
    resolvedItems,
    offeredFormats,
    override,
    setOverride,
    selectedOptions,
    toggleOption,
    imageFileType,
    setImageFileType,
    pdfImageFileType,
    setPdfImageFileType,
    videoSpec,
    setVideoSpec,
    showFormats,
    showStaticDisclosures,
    showImageSettings: showFormats || showStaticDisclosures,
    showVideoSettings: videoAssets !== undefined,
    canConfirm: !showFormats || selectedCount > 0,
    buildResult,
  };
}

export type ExportSettingsState = ReturnType<typeof useExportSettingsState>;
